/**
 * JUMP STRAIGHT INTO A LEVEL — DIVISION-WIDE HOMESITE / FLOORPLAN SEARCH. 2026-08-22.
 *
 * Jack: "would like to jump straight into each section … go direct to homesite level
 * when clicking on it rather than select communities then select floor plans and etc …
 * should be able to search for them in the search bar via name, street address, and
 * homesite number like 26254600130."
 *
 * WHAT THIS IS. The wizard's Floorplans and Homesites steps used to be reachable only
 * by walking Communities → Floorplans → Homesites, because their inventory came from
 * `/sfmc-content?community=CODE` — one call per community, so a division-wide list
 * meant ~50 calls. Two worker routes make one call enough:
 *
 *   • `/homesites.json?div=JAX`  — available homes, whole division (139 in JAX)
 *   • `/plans.json?div=JAX&all=1` — floorplans selling now AND coming soon, whole
 *                                  division (196 + 170 in JAX, 2026-10-22); sold out dropped
 *
 * Both are shaped into the SAME `Pack` the picker already renders, so the grid, the
 * Cards ⇄ List toggle, the grouping by community and the selection logic are untouched.
 *
 * WHY PLANS AND NOT JUST HOMES (Jack, same thread): "the homesites are getting their
 * images from the plan level, that should be much richer than homesites." Right — the
 * plans feed carries hero, elevation, floorplan overlay, kitchen, virtual tour, EI URL,
 * beds/baths/sqft and starting price; a homesite row only borrows the plan's hero. And
 * "there's also a status that should show plans as active which are the ones you should
 * be pulling in/filtering from the rest" — `/plans.json` filters `plan_status ACTIVE`
 * server-side (1,580 non-ACTIVE dropped nationally on the 8/22 measurement).
 *
 * ── THE ONE THING THAT IS NOT COSMETIC ──────────────────────────────────────────────
 * A home's stored pick is `homeKey()` = `code|h|addr|price`, and it is VERIFIED against
 * the packs in hand at read time (`homePickList`) — a key that matches nothing is
 * dropped. The two feeds do not agree on price: `/homesites.json` reads the Sitecore
 * DE while `/sfmc-content` prefers the Salesforce-enriched DE, so a home picked from
 * the division feed could store a key the real pack cannot match, and the pick would
 * vanish on the next fill. That is why `resolveDirect()` exists: on Done the real
 * community packs are fetched and every selected key is REMAPPED by LOT — the one
 * identifier both feeds spell identically. Plans are safe by identity already
 * (`planPickKey` stores num+name, both from the same Plans DE), but they go through
 * the same resolve so the fill only ever sees real packs.
 */
import { getCommunityPack, getInventoryFeed } from "./api";
import { scrubPackImages } from "./echLiveImg";
import { homeKey, type Pack } from "./dynamicFill";
import type { CommunityPackResponse, HomesiteInfo, PlanInfo } from "../types/api";

/** the two levels that can be entered directly; "comms" already needs no drill-down */
export type DirectLevel = "plans" | "homes";

interface DirectHomeRow {
  lot?: string; commCode?: string; community?: string; commHero?: string;
  plan?: string; planNo?: string; address?: string; city?: string; state?: string;
  price?: number; wasPrice?: number; beds?: number; baths?: number; halfBaths?: number;
  sqft?: number; status?: string; url?: string; img?: string; imgSrc?: string;
  hotw?: boolean; nextGen?: boolean; moveIn?: string; division?: string; market?: string;
}
interface DirectPlanRow {
  num?: string; name?: string; commCode?: string; community?: string; commHero?: string;
  url?: string; tour?: string; ei?: string; hero?: string; elevation?: string;
  floorplan?: string; kitchen?: string; beds?: number; baths?: number; sqft?: number;
  price?: number; status?: string; division?: string;
}

/** an empty pack body, so a synthetic pack is type-identical to a fetched one */
const emptyC = (code: string, name: string, hero: string): CommunityPackResponse => ({
  ok: true, code,
  community: { code, name, hero, url: "" },
  plans: [], homesites: [], counts: { plans: 0, homesites: 0, rawHomesites: 0 },
} as unknown as CommunityPackResponse);

/**
 * One division call → packs grouped by community, in the picker's own shape.
 *
 * Communities arrive sorted by name (the worker sorts plans; homes are grouped here)
 * because the picker renders one heading per pack in array order and an alphabetical
 * list is the only order a requester can scan.
 */
export async function fetchDirect(level: DirectLevel, div: string): Promise<Pack[]> {
  const d = String(div || "").trim().toUpperCase();
  if (!d) return [];
  /* Through the api-layer (getInventoryFeed) so it follows BASE + gateway auth —
     no direct WORKER_BASE fetch. Worker mode still resolves to the worker; gateway
     mode routes /homesites.json + /plans.json through the gateway like the rest. */
  const j = await getInventoryFeed(level, d);
  const rows = (Array.isArray(j?.items) ? j.items : []) as (DirectHomeRow | DirectPlanRow)[];
  const byCode: Record<string, Pack> = {};
  const packFor = (code: string, name: string, hero: string): Pack => {
    const key = String(code || name || "?");
    if (!byCode[key]) byCode[key] = { name: name || key, code: String(code || ""), C: emptyC(String(code || ""), name || key, hero || "") };
    /* a later row may carry the hero the first one lacked */
    const P = byCode[key];
    if (hero && P.C.community && !P.C.community.hero) P.C.community.hero = hero;
    return P;
  };
  if (level === "homes") {
    (rows as DirectHomeRow[]).forEach((h) => {
      if (!h || (!h.address && !h.plan)) return;
      const P = packFor(String(h.commCode || ""), String(h.community || ""), String(h.commHero || ""));
      const hm: HomesiteInfo & { lot?: string; market?: string } = {
        addr: h.address || "", plan: h.plan || "", city: h.city || "", state: h.state || "",
        sqft: h.sqft, beds: h.beds, baths: h.baths, price: h.price || undefined,
        wasPrice: h.wasPrice || undefined, url: h.url || "", img: h.img || "",
        status: h.status || "", hotw: !!h.hotw, nextGen: !!h.nextGen, moveIn: h.moveIn || "",
        imgSrc: h.imgSrc || "", lot: String(h.lot || ""),
        /* the row's own MARKET code — carried so a Market-level request can pick the
           communities inside that market without a second feed (2026-08-22) */
        market: String(h.market || "").toUpperCase(),
      } as HomesiteInfo & { lot?: string; market?: string };
      (P.C.homesites as (HomesiteInfo & { lot?: string })[]).push(hm);
    });
  } else {
    /* ── COMING-SOON FLOORPLANS ARE LISTED (2026-10-22, Gabriella Torres, JAX) ─────────
       "Renderings that are online for a JAX community are not pulling in the email
       hub." The feed was asked for ACTIVE plans only, so every COMING_SOON plan was
       missing from this step while its renderings were already on lennar.com —
       measured on dev the same day: 1,621 of 13,778 plans hidden across 38 of 46
       divisions (JAX 170 of 366), and 267 collections whose plans are ALL coming soon
       listed nothing at all (JAX: Diamond Springs, Lakeview Estates, Cove at
       Southwood…). The route is asked with `all=1`; SOLD_OUT is dropped here; within a
       community the plans selling now come first. The card carries the status, so the
       picker tags a coming-soon plan (statusChip). */
    const rank = (s?: string) => (String(s || "").toUpperCase() === "COMING_SOON" ? 1 : 0);
    (rows as DirectPlanRow[])
      .filter((p) => !!p && String(p.status || "").toUpperCase() !== "SOLD_OUT")
      .map((p, i) => ({ p, i }))
      .sort((a, b) => (String(a.p.commCode || a.p.community || "") === String(b.p.commCode || b.p.community || "")
        ? (rank(a.p.status) - rank(b.p.status)) || (a.i - b.i) : a.i - b.i))
      .map((x) => x.p)
      .forEach((p) => {
      if (!p || !p.name) return;
      const P = packFor(String(p.commCode || ""), String(p.community || ""), String(p.commHero || ""));
      const pl: PlanInfo = {
        num: String(p.num || ""), name: p.name || "", url: p.url || "", tour: p.tour || "",
        hero: p.hero || "", elevation: p.elevation || "", floorplan: p.floorplan || "",
        kitchen: p.kitchen || "", beds: p.beds, baths: p.baths, sqft: p.sqft,
        price: p.price || undefined, status: p.status || "", ei: p.ei || "",
      };
      (P.C.plans as PlanInfo[]).push(pl);
    });
  }
  const out = Object.keys(byCode).map((k) => byCode[k]);
  out.forEach((p) => { p.C.counts = { plans: (p.C.plans || []).length, homesites: (p.C.homesites || []).length, rawHomesites: (p.C.homesites || []).length }; });
  out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  /* the division feed is the other door into the fill — same dead-URL scrub as the
     community pack (echLiveImg), so a card built from Add by ID / Market scope
     cannot ship a photo the CDN no longer serves */
  await Promise.all(out.map(async (p) => { p.C = await scrubPackImages(p.C); }));
  return out;
}

/**
 * THE SELECTED KEYS, REMAPPED ONTO REAL PACKS — the correctness half of this feature.
 *
 * Fetches `/sfmc-content` for every community the requester picked from and rewrites
 * each selected key against those rows: homes by LOT (identical in both feeds), plans
 * by plan number then name. A selection whose community pack fails to load, or whose
 * row is not in the real pack, is DROPPED and reported — an unresolvable pick stored
 * anyway is the vanishing-pick fault this whole function exists to prevent.
 */
export async function resolveDirect(
  level: DirectLevel,
  synth: Pack[],
  keys: string[],
): Promise<{ packs: Pack[]; keys: string[]; dropped: string[] }> {
  /* DEDUPED (audit 2026-08-25): `outKeys` is deduped below, but the carry-over PUSHES a
     row into the resolved pack — so a repeated key appended the same home twice. */
  const want = Array.from(new Set((keys || []).filter(Boolean)));
  if (!want.length) return { packs: [], keys: [], dropped: [] };
  /* which community each key belongs to, and what identifies the row inside it */
  type Sel = { code: string; key: string; lot?: string; num?: string; name?: string };
  const sels: Sel[] = [];
  want.forEach((k) => {
    const parts = String(k).split("|");
    const code = parts[0] || "";
    const P = synth.find((p) => p.code === code);
    if (!P) return;
    if (level === "homes") {
      const hm = (P.C.homesites || []).find((h) => homeKey(P.code, h) === k) as (HomesiteInfo & { lot?: string }) | undefined;
      if (hm) sels.push({ code, key: k, lot: String(hm.lot || ""), name: hm.addr || "" });
    } else {
      const pl = (P.C.plans || [])[Number(parts[2])];
      if (pl) sels.push({ code, key: k, num: String(pl.num || ""), name: String(pl.name || "") });
    }
  });
  const codes = Array.from(new Set(sels.map((s) => s.code))).filter(Boolean);
  const packs: Pack[] = [];
  const dropped: string[] = [];
  await Promise.all(codes.map(async (code) => {
    try {
      const C = await getCommunityPack(code);
      const nm = (C && C.community && C.community.name)
        || (synth.find((p) => p.code === code) || { name: code }).name;
      packs.push({ name: nm, code, C });
    } catch (e) { /* reported through `dropped` below */ }
  }));
  const outKeys: string[] = [];
  sels.forEach((s) => {
    const P = packs.find((p) => p.code === s.code);
    if (!P) { dropped.push(s.name || s.key); return; }
    if (level === "homes") {
      const rows = (P.C.homesites || []) as (HomesiteInfo & { lot?: string })[];
      /* LOT is the join. Address is the fallback for a feed row with no lot id — it is
         unique within a community, which is all this needs to be. */
      const hit = rows.find((h) => s.lot && String(h.lot || "") === s.lot)
        || rows.find((h) => s.name && String(h.addr || "").toLowerCase() === s.name.toLowerCase());
      if (!hit) {
        /* ── A HOME THE REAL PACK DOES NOT LIST IS CARRIED OVER, NOT DROPPED ─────────
           (2026-08-25.) A division, via Feedback: "have 6 homes selected for listing
           table and it only added 1 to the table." The homesites step lists the WHOLE
           division from `/homesites.json`; `/sfmc-content` returns at most THIRTY
           homesites per community (worker `homesites.slice(0, 30)`), so a pick outside
           that slice found no match here and was silently dropped — the more communities
           in the selection, the fewer picks survived.
           The direct feed row is real inventory with every field the table needs
           (address, plan, sqft, beds, baths, price, url, photo), so it is APPENDED to
           the resolved pack and keyed from there. Nothing is invented and nothing is
           guessed: it is the same row the requester chose, from the same feed the
           picker drew it from. */
        const syn = synth.find((p) => p.code === s.code);
        const row = syn && ((syn.C.homesites || []) as (HomesiteInfo & { lot?: string })[])
          .find((h) => homeKey(syn.code, h) === s.key);
        if (!row) { dropped.push(s.name || s.key); return; }
        const list = (P.C.homesites || []) as (HomesiteInfo & { lot?: string })[];
        list.push(row);
        P.C.homesites = list as never;
        const k2s = homeKey(P.code, row);
        if (outKeys.indexOf(k2s) < 0) outKeys.push(k2s);
        return;
      }
      const k2 = homeKey(P.code, hit);
      if (outKeys.indexOf(k2) < 0) outKeys.push(k2);
    } else {
      const rows = (P.C.plans || []) as PlanInfo[];
      const idx = rows.findIndex((pl) => (s.num && String(pl.num || "") === s.num))
        !== -1
        ? rows.findIndex((pl) => (s.num && String(pl.num || "") === s.num))
        : rows.findIndex((pl) => s.name && String(pl.name || "").toLowerCase() === s.name.toLowerCase());
      if (idx < 0) {
        /* ── A PLAN THE REAL PACK DOES NOT LIST IS CARRIED OVER, NOT DROPPED ─────────
           (2026-10-22.) Same shape as the homesite carry-over above: `/sfmc-content`
           returns at most TWELVE plans per community, and this step lists the whole
           division, so a pick past the twelfth found no match and was dropped. Coming-
           soon plans made it common (Diamond Springs 50s: 15 plans). The division feed
           row is real inventory, so it is appended to the resolved pack and keyed. */
        const syn = synth.find((p) => p.code === s.code);
        const row = syn && (syn.C.plans || []).find((pl) => (s.num && String(pl.num || "") === s.num)
          || (!s.num && s.name && String(pl.name || "").toLowerCase() === s.name.toLowerCase()));
        if (!row) { dropped.push(s.name || s.key); return; }
        const list = (P.C.plans || []) as PlanInfo[];
        list.push(row);
        P.C.plans = list as never;
        const k3 = P.code + "|p|" + (list.length - 1);
        if (outKeys.indexOf(k3) < 0) outKeys.push(k3);
        return;
      }
      const k2 = P.code + "|p|" + idx;
      if (outKeys.indexOf(k2) < 0) outKeys.push(k2);
    }
  });
  return { packs, keys: outKeys, dropped };
}
